import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse
} from '@angular/common/http';
import { catchError, switchMap, filter, take, finalize } from 'rxjs/operators';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { Router } from '@angular/router';
import { UserInfoService } from '../../services/UserInfoService/user-info.service';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private isRefreshing = false;
  private loadingElement: HTMLElement | null = null;
  private activeRequests = 0;
  private loadingTimer: any;
  private spinnerVisible = false;

  constructor(private userInfoService: UserInfoService, private router: Router) {
    this.createLoadingElement();
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let authReq = request;
    const token = this.userInfoService.getAccessToken();
    if (token != null) {
      authReq = this.addToken(request, token);
    }

    this.activeRequests++;

    if (this.activeRequests === 1) {
      this.loadingTimer = setTimeout(() => {
        if (this.activeRequests > 0) {
          this.showLoading();
        }
      }, 1000);
    }

    return next.handle(authReq).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          return this.handle401Error(authReq, next);
        }
        return throwError(error);
      }),
      finalize(() => {
        this.activeRequests--;
        if (this.activeRequests === 0) {
          clearTimeout(this.loadingTimer);
          this.hideLoading();
        }
      })
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.userInfoService.refreshToken().pipe(
        switchMap((tokenResponse: any) => {
          this.isRefreshing = false;
          const newToken = tokenResponse.accessToken;
          this.userInfoService.setAccessToken(newToken);
          this.refreshTokenSubject.next(newToken);
          return next.handle(this.addToken(request, newToken));
        }),
        catchError((error: any) => {
          this.logoutAndNavigateToProfile();
          this.router.navigate(['/ar/login']);
          return throwError(error);
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((jwt) => {
          return next.handle(this.addToken(request, jwt));
        })
      );
    }
  }

  private addToken(request: HttpRequest<any>, token: string) {
    return request.clone({
      setHeaders: { Authorization: `Bearer ${token}` }
    });
  }

  private logoutAndNavigateToProfile(): void {
    if (this.userInfoService.isUserLoggedIn()) {
      this.userInfoService.clearUserInfo();
      this.router.navigate(['/ar/login']);
    }
  }

  private createLoadingElement() {
    this.loadingElement = document.createElement('div');
    this.loadingElement.id = 'loading-overlay';
    this.loadingElement.innerHTML = `<div class="spinner"></div>`;
    Object.assign(this.loadingElement.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      background: 'rgba(0,0,0,0.4)',
      display: 'none',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: '9999'
    });
    document.body.appendChild(this.loadingElement);

    const style = document.createElement('style');
    style.textContent = `
      .spinner {
        width: 50px;
        height: 50px;
        border: 5px solid #ccc;
        border-top: 5px solid #2196f3;
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
      }
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
  }

  private showLoading() {
    if (this.loadingElement && !this.spinnerVisible) {
      this.loadingElement.style.display = 'flex';
      this.spinnerVisible = true;
    }
  }

  private hideLoading() {
    if (this.loadingElement && this.spinnerVisible) {
      this.loadingElement.style.display = 'none';
      this.spinnerVisible = false;
    }
  }
}
